/**
 * actionExecutor.js
 * =================
 * Utility for executing configured actions
 * Handles all action types with proper error handling
 * 
 * Created: 2025-11-20
 * Updated: 2025-11-21 - Added new static website actions
 */

import { createLogger } from './logger';

const logger = createLogger('utils/actionExecutor.js');

/**
 * Execute an action based on its configuration
 * @param {Object} actionConfig - Complete action configuration
 * @param {Object} runtimeContext - Runtime context (current page, layout, modal controller)
 * @returns {Promise<boolean>} - True if execution succeeded
 */
export const executeAction = async (actionConfig, runtimeContext = {}) => {
  if (!actionConfig || !actionConfig.actionType) {
    logger.error('executeAction', 'Invalid action configuration', actionConfig);
    console.error('❌ Invalid action configuration:', actionConfig);
    return false;
  }

  // Support both old format (actionConfig.actionConfig) and new format (actionConfig directly)
  const config = actionConfig.actionConfig || actionConfig;
  const actionType = actionConfig.actionType || config.actionType || actionConfig.actionType;
  
  logger.entry('executeAction', { actionType, config });
  console.group(`🚀 Executing Action: ${actionType}`);
  console.log('Action Config:', config);

  try {
    switch (actionType) {
      case 'openPage':
        return await executeOpenPage(config, runtimeContext);
      
      case 'scrollToSection':
        return await executeScrollToSection(config);
      
      case 'showContactForm':
        return await executeShowContactForm(config);
      
      case 'downloadBrochure':
        return await executeDownloadBrochure(config);
      
      case 'openWhatsApp':
        return await executeOpenWhatsApp(config);
      
      case 'makeCall':
        return await executeMakeCall(config);
      
      case 'sendEmail':
        return await executeSendEmail(config);
      
      case 'copyToClipboard':
        return await executeCopyToClipboard(config);
      
      case 'toggleElement':
        return await executeToggleElement(config);
      
      case 'playAnimation':
        return await executePlayAnimation(config);
      
      case 'showBanner':
        return await executeShowBanner(config);
      
      case 'showSubscribePopup':
        return await executeShowSubscribePopup(config);
      
      case 'showCouponCard':
        return await executeShowCouponCard(config);
      
      case 'showQRPopup':
        return await executeShowQRPopup(config);
      
      default:
        logger.warn('executeAction', `Unknown action type: ${actionType}`);
        console.warn(`⚠️ Unknown action type: ${actionType}`);
        return false;
    }
  } catch (error) {
    logger.error('executeAction', `Action execution failed: ${actionType}`, error);
    console.error('❌ Action execution failed:', error);
    return false;
  } finally {
    console.groupEnd();
    logger.exit('executeAction', { actionType });
  }
};

/**
 * Find primary content section from layout
 * Returns: 'content', 'body', 'main', or 'main-content' based on layout definition
 */
const findPrimaryContentSection = (layoutId) => {
  // Import layout config dynamically to avoid circular dependencies
  try {
    const { getLayout } = require('../config/layouts');
    const layout = getLayout(layoutId);
    
    if (layout && layout.slots) {
      // Look for primary content slots in order of preference
      const primarySlots = ['content', 'body', 'main', 'main-content'];
      
      for (const slotName of primarySlots) {
        const slot = layout.slots.find(s => 
          s.id === slotName || 
          s.name?.toLowerCase().includes('content') ||
          s.name?.toLowerCase().includes('main')
        );
        if (slot) {
          return slot.id;
        }
      }
      
      // If no primary slot found, return first required slot or first slot
      const requiredSlot = layout.slots.find(s => s.required);
      if (requiredSlot) return requiredSlot.id;
      
      if (layout.slots.length > 0) return layout.slots[0].id;
    }
  } catch (error) {
    console.warn('Could not load layout config:', error);
  }
  
  // Fallback to common slot names
  return 'content' || 'body' || 'main';
};

/**
 * Open a page (internal or external URL)
 * Simplified action runner with three modes: CONTENT, POPUP, FULLSCREEN
 */
const executeOpenPage = async (config, runtimeContext = {}) => {
  const { 
    openTarget = 'INTERNAL',  // INTERNAL or EXTERNAL
    openMode = 'CONTENT',     // CONTENT, POPUP, or FULLSCREEN (only for INTERNAL)
    targetPageId,              // Page ID/name for internal pages
    pageName,                  // Backward compatibility
    url,                       // URL for external pages
    newTab = false,            // Open in new tab (for external)
    popupConfig = {}           // Popup configuration (size, etc.)
  } = config;

  try {
    // Handle external pages
    if (openTarget === 'EXTERNAL') {
      if (!url) {
        console.error('❌ No URL provided for external navigation');
        return false;
      }

      if (newTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
        console.log(`✅ Opened ${url} in new tab`);
      } else {
        window.location.href = url;
        console.log(`✅ Navigating to ${url}`);
      }
      return true;
    }

    // Handle internal pages
    const targetPage = targetPageId || pageName;
    if (!targetPage) {
      console.error('❌ No page ID provided for internal navigation');
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const appName = urlParams.get('app') || localStorage.getItem('currentAppName') || runtimeContext.appName;

    if (!appName) {
      console.error('❌ App name not found for internal navigation');
      alert('❌ Unable to navigate: App context not found');
      return false;
    }

    // Get current layout from runtime context or try to detect it
    const currentLayoutId = runtimeContext.layoutId || 
                           document.querySelector('[data-layout-id]')?.getAttribute('data-layout-id') ||
                           'single-column';

    // Execute based on openMode
    if (openMode === 'CONTENT') {
      // Find primary content section from layout
      const primarySlot = findPrimaryContentSection(currentLayoutId);
      
      // Check if we're in AppPreview (slot-based navigation enabled)
      const isAppPreview = window.location.pathname.includes('/app-preview');
      
      if (isAppPreview) {
        // Use slot-based navigation to primary content section
        const navigationEvent = new CustomEvent('slotNavigation', {
          detail: {
            targetPage: targetPage,
            targetSlot: primarySlot,
            source: 'action'
          }
        });
        window.dispatchEvent(navigationEvent);
        console.log(`✅ Content navigation: ${targetPage} → ${primarySlot} slot`);
        return true;
      } else {
        // Fallback to full page navigation
        const previewUrl = `/app-preview?app=${encodeURIComponent(appName)}&page=${encodeURIComponent(targetPage)}`;
        window.location.href = previewUrl;
        console.log(`✅ Full page navigation to: ${targetPage}`);
        return true;
      }
    } 
    
    else if (openMode === 'POPUP') {
      // Open page in modal/popup
      const popupSize = popupConfig.size || 'medium';
      const sizeMap = {
        small: { width: '400px', height: '300px' },
        medium: { width: '600px', height: '500px' },
        large: { width: '900px', height: '700px' }
      };
      const dimensions = sizeMap[popupSize] || sizeMap.medium;
      
      // Dispatch modal open event
      const modalEvent = new CustomEvent('openPageModal', {
        detail: {
          targetPage: targetPage,
          appName: appName,
          size: popupSize,
          dimensions: dimensions
        }
      });
      window.dispatchEvent(modalEvent);
      console.log(`✅ Popup navigation: ${targetPage} (${popupSize})`);
      return true;
    } 
    
    else if (openMode === 'FULLSCREEN') {
      // Replace entire page view
      const previewUrl = `/app-preview?app=${encodeURIComponent(appName)}&page=${encodeURIComponent(targetPage)}`;
      window.location.href = previewUrl;
      console.log(`✅ Fullscreen navigation to: ${targetPage}`);
      return true;
    }

    // Fallback to CONTENT mode if openMode is invalid
    console.warn(`⚠️ Invalid openMode: ${openMode}, falling back to CONTENT`);
    return await executeOpenPage({ ...config, openMode: 'CONTENT' }, runtimeContext);
    
  } catch (error) {
    console.error('❌ Failed to open page:', error);
    return false;
  }
};

/**
 * Scroll to a section on the page
 */
const executeScrollToSection = async (config) => {
  const { sectionId, behavior = 'smooth' } = config;

  if (!sectionId) {
    console.error('❌ No section ID provided for scrollToSection action');
    return false;
  }

  try {
    const element = document.getElementById(sectionId);
    
    if (!element) {
      console.warn(`⚠️ Section with ID "${sectionId}" not found`);
      alert(`Section "${sectionId}" not found on this page`);
      return false;
    }

    element.scrollIntoView({
      behavior: behavior,
      block: 'start',
    });

    console.log(`✅ Scrolled to section: ${sectionId}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to scroll to section:', error);
    return false;
  }
};

/**
 * Show a contact form
 */
const executeShowContactForm = async (config) => {
  const { formId, displayType = 'modal' } = config;

  if (!formId) {
    console.error('❌ No form ID provided for showContactForm action');
    return false;
  }

  try {
    console.log(`✅ Showing contact form (${displayType}): ${formId}`);
    alert(`Contact Form would be displayed as: ${displayType}\nForm ID: ${formId}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to show contact form:', error);
    return false;
  }
};

/**
 * Download a file (brochure, PDF, etc.)
 */
const executeDownloadBrochure = async (config) => {
  const { fileUrl, fileName = 'download.pdf' } = config;

  if (!fileUrl) {
    console.error('❌ No file URL provided for downloadBrochure action');
    return false;
  }

  try {
    console.log(`✅ Downloading file: ${fileName} from ${fileUrl}`);
    
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`✅ Download initiated: ${fileName}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to download file:', error);
    return false;
  }
};

/**
 * Open WhatsApp with pre-filled message
 */
const executeOpenWhatsApp = async (config) => {
  const { phoneNumber, message = '' } = config;

  if (!phoneNumber) {
    console.error('❌ No phone number provided for WhatsApp');
    return false;
  }

  try {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${cleanNumber}${message ? `?text=${encodedMessage}` : ''}`;
    
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    console.log(`✅ Opening WhatsApp for: ${phoneNumber}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to open WhatsApp:', error);
    return false;
  }
};

/**
 * Initiate phone call
 */
const executeMakeCall = async (config) => {
  const { phoneNumber } = config;

  if (!phoneNumber) {
    console.error('❌ No phone number provided for call');
    return false;
  }

  try {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    window.location.href = `tel:${cleanNumber}`;
    console.log(`✅ Initiating call to: ${phoneNumber}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to initiate call:', error);
    return false;
  }
};

/**
 * Open email client
 */
const executeSendEmail = async (config) => {
  const { email, subject = '', body = '' } = config;

  if (!email) {
    console.error('❌ No email address provided');
    return false;
  }

  try {
    const params = new URLSearchParams();
    if (subject) params.append('subject', subject);
    if (body) params.append('body', body);
    
    const mailtoUrl = `mailto:${email}${params.toString() ? '?' + params.toString() : ''}`;
    window.location.href = mailtoUrl;
    console.log(`✅ Opening email client for: ${email}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to open email client:', error);
    return false;
  }
};

/**
 * Copy text to clipboard
 */
const executeCopyToClipboard = async (config) => {
  const { textToCopy, successMessage = 'Copied to clipboard!' } = config;

  if (!textToCopy) {
    console.error('❌ No text provided to copy');
    return false;
  }

  try {
    await navigator.clipboard.writeText(textToCopy);
    alert(successMessage);
    console.log(`✅ Copied to clipboard: ${textToCopy}`);
    return true;
  } catch (error) {
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(successMessage);
      console.log(`✅ Copied to clipboard (fallback): ${textToCopy}`);
      return true;
    } catch (fallbackError) {
      console.error('❌ Failed to copy to clipboard:', fallbackError);
      return false;
    }
  }
};

/**
 * Toggle element visibility
 */
const executeToggleElement = async (config) => {
  const { targetElementId, animationType = 'fade' } = config;

  if (!targetElementId) {
    console.error('❌ No target element ID provided');
    return false;
  }

  try {
    const element = document.getElementById(targetElementId);
    
    if (!element) {
      console.warn(`⚠️ Element with ID "${targetElementId}" not found`);
      return false;
    }

    const isHidden = element.style.display === 'none' || !element.offsetParent;

    if (animationType === 'none') {
      element.style.display = isHidden ? 'block' : 'none';
    } else if (animationType === 'fade') {
      if (isHidden) {
        element.style.display = 'block';
        element.style.opacity = '0';
        setTimeout(() => {
          element.style.transition = 'opacity 0.3s';
          element.style.opacity = '1';
        }, 10);
      } else {
        element.style.transition = 'opacity 0.3s';
        element.style.opacity = '0';
        setTimeout(() => {
          element.style.display = 'none';
        }, 300);
      }
    } else if (animationType === 'slide') {
      if (isHidden) {
        element.style.display = 'block';
        element.style.maxHeight = '0';
        element.style.overflow = 'hidden';
        setTimeout(() => {
          element.style.transition = 'max-height 0.3s ease-out';
          element.style.maxHeight = '1000px';
        }, 10);
      } else {
        element.style.transition = 'max-height 0.3s ease-out';
        element.style.maxHeight = '0';
        setTimeout(() => {
          element.style.display = 'none';
        }, 300);
      }
    }

    console.log(`✅ Toggled element: ${targetElementId}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to toggle element:', error);
    return false;
  }
};

/**
 * Play animation (Lottie or CSS)
 */
const executePlayAnimation = async (config) => {
  const { animationType, targetElementId, lottieUrl, loop = false } = config;

  if (!targetElementId) {
    console.error('❌ No target element ID provided');
    return false;
  }

  try {
    const element = document.getElementById(targetElementId);
    
    if (!element) {
      console.warn(`⚠️ Element with ID "${targetElementId}" not found`);
      return false;
    }

    if (animationType === 'lottie') {
      console.log(`✅ Lottie animation would play from: ${lottieUrl}`);
      alert(`Lottie animation integration needed.\nURL: ${lottieUrl}\nTarget: ${targetElementId}\nLoop: ${loop}`);
    } else {
      element.style.animation = 'none';
      setTimeout(() => {
        element.style.animation = '';
      }, 10);
      console.log(`✅ CSS animation replayed on: ${targetElementId}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Failed to play animation:', error);
    return false;
  }
};

/**
 * Show banner/ribbon
 */
const executeShowBanner = async (config) => {
  const { 
    bannerText, 
    position = 'top', 
    bgColor = '#3B82F6', 
    textColor = '#FFFFFF',
    dismissible = true 
  } = config;

  if (!bannerText) {
    console.error('❌ No banner text provided');
    return false;
  }

  try {
    const bannerId = 'action-banner-' + Date.now();
    const banner = document.createElement('div');
    banner.id = bannerId;
    banner.innerHTML = `
      <div style="
        background: ${bgColor};
        color: ${textColor};
        padding: 12px 20px;
        text-align: center;
        position: fixed;
        ${position}: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      ">
        <span>${bannerText}</span>
        ${dismissible ? '<button onclick="this.parentElement.parentElement.remove()" style="background: transparent; border: none; color: inherit; cursor: pointer; font-size: 18px; padding: 0 8px;">✕</button>' : ''}
      </div>
    `;
    
    document.body.appendChild(banner);
    console.log(`✅ Banner displayed at ${position}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to show banner:', error);
    return false;
  }
};

/**
 * Show subscribe popup
 */
const executeShowSubscribePopup = async (config) => {
  const { 
    title = 'Subscribe to Our Newsletter', 
    description = 'Get the latest updates!',
    buttonText = 'Subscribe',
    formAction = ''
  } = config;

  try {
    const popupId = 'subscribe-popup-' + Date.now();
    const popup = document.createElement('div');
    popup.id = popupId;
    popup.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      " onclick="if(event.target === this) this.remove()">
        <div style="
          background: white;
          padding: 32px;
          border-radius: 12px;
          max-width: 400px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        " onclick="event.stopPropagation()">
          <button onclick="document.getElementById('${popupId}').remove()" style="
            float: right;
            background: transparent;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
          ">✕</button>
          <h2 style="margin: 0 0 12px 0; color: #111;">${title}</h2>
          <p style="margin: 0 0 20px 0; color: #666;">${description}</p>
          <input type="email" placeholder="Enter your email" style="
            width: 100%;
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 6px;
            margin-bottom: 12px;
            box-sizing: border-box;
            font-size: 14px;
          ">
          <button style="
            width: 100%;
            background: #3B82F6;
            color: white;
            border: none;
            padding: 12px;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
          ">${buttonText}</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(popup);
    console.log(`✅ Subscribe popup displayed`);
    return true;
  } catch (error) {
    console.error('❌ Failed to show subscribe popup:', error);
    return false;
  }
};

/**
 * Show coupon/offer card
 */
const executeShowCouponCard = async (config) => {
  const { 
    couponCode, 
    offerTitle = 'Special Offer!',
    offerDescription = 'Limited time only',
    autoCopy = true 
  } = config;

  if (!couponCode) {
    console.error('❌ No coupon code provided');
    return false;
  }

  try {
    const cardId = 'coupon-card-' + Date.now();
    const card = document.createElement('div');
    card.id = cardId;
    card.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      " onclick="if(event.target === this) this.remove()">
        <div style="
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 32px;
          border-radius: 16px;
          max-width: 400px;
          width: 90%;
          color: white;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        " onclick="event.stopPropagation()">
          <button onclick="document.getElementById('${cardId}').remove()" style="
            float: right;
            background: transparent;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: white;
          ">✕</button>
          <div style="font-size: 48px; margin-bottom: 12px;">🎉</div>
          <h2 style="margin: 0 0 8px 0;">${offerTitle}</h2>
          <p style="margin: 0 0 20px 0; opacity: 0.9;">${offerDescription}</p>
          <div style="
            background: white;
            color: #667eea;
            padding: 16px;
            border-radius: 8px;
            font-size: 24px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 12px;
          ">${couponCode}</div>
          <button onclick="
            navigator.clipboard.writeText('${couponCode}');
            this.innerText = '✓ Copied!';
            setTimeout(() => this.innerText = 'Copy Code', 2000);
          " style="
            background: rgba(255,255,255,0.2);
            color: white;
            border: 2px solid white;
            padding: 10px 24px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
          ">Copy Code</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(card);
    
    if (autoCopy) {
      navigator.clipboard.writeText(couponCode);
      setTimeout(() => alert(`Coupon code "${couponCode}" copied!`), 500);
    }
    
    console.log(`✅ Coupon card displayed: ${couponCode}`);
    return true;
  } catch (error) {
    console.error('❌ Failed to show coupon card:', error);
    return false;
  }
};

/**
 * Show QR code popup
 */
const executeShowQRPopup = async (config) => {
  const { 
    qrContent, 
    title = 'Scan QR Code',
    description = 'Scan with your phone',
    qrSize = 'medium' 
  } = config;

  if (!qrContent) {
    console.error('❌ No QR content provided');
    return false;
  }

  try {
    const sizeMap = { small: 128, medium: 256, large: 512 };
    const size = sizeMap[qrSize] || 256;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(qrContent)}`;
    
    const popupId = 'qr-popup-' + Date.now();
    const popup = document.createElement('div');
    popup.id = popupId;
    popup.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
      " onclick="if(event.target === this) this.remove()">
        <div style="
          background: white;
          padding: 32px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        " onclick="event.stopPropagation()">
          <button onclick="document.getElementById('${popupId}').remove()" style="
            float: right;
            background: transparent;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #666;
          ">✕</button>
          <h2 style="margin: 0 0 8px 0; color: #111;">${title}</h2>
          <p style="margin: 0 0 20px 0; color: #666;">${description}</p>
          <img src="${qrUrl}" alt="QR Code" style="
            display: block;
            margin: 0 auto;
            border: 8px solid #f5f5f5;
            border-radius: 8px;
          ">
        </div>
      </div>
    `;
    
    document.body.appendChild(popup);
    console.log(`✅ QR popup displayed`);
    return true;
  } catch (error) {
    console.error('❌ Failed to show QR popup:', error);
    return false;
  }
};

/**
 * Check if an action can be previewed visually
 */
export const isActionPreviewable = (actionType) => {
  const nonPreviewableActions = [
    'openPage',
    'downloadBrochure',
    'openWhatsApp',
    'makeCall',
    'sendEmail',
    'copyToClipboard'
  ];
  
  return !nonPreviewableActions.includes(actionType);
};

/**
 * Get a user-friendly message for actions
 */
export const getPreviewMessage = (actionType, actionConfig = {}) => {
  if (actionType === 'openPage') {
    const openTarget = actionConfig?.openTarget || 'INTERNAL';
    if (openTarget === 'EXTERNAL') {
      const url = actionConfig?.url || 'external URL';
      const newTab = actionConfig?.newTab ? ' (in new tab)' : '';
      return `This action will open: ${url}${newTab}`;
    } else {
      const pageName = actionConfig?.targetPageId || actionConfig?.pageName || 'selected page';
      const openMode = actionConfig?.openMode || 'CONTENT';
      const modeText = {
        'CONTENT': 'change the main content',
        'POPUP': 'open in a popup',
        'FULLSCREEN': 'replace the full screen'
      }[openMode] || 'navigate to';
      return `This action will ${modeText}: ${pageName}`;
    }
  }
  
  const messages = {
    downloadBrochure: 'This action will download a file when clicked.',
    showContactForm: 'This action will display a contact form.',
    scrollToSection: 'This action will scroll to the specified section on the page.',
    openWhatsApp: `This action will open WhatsApp${actionConfig?.phoneNumber ? ` to ${actionConfig.phoneNumber}` : ''}.`,
    makeCall: `This action will initiate a phone call${actionConfig?.phoneNumber ? ` to ${actionConfig.phoneNumber}` : ''}.`,
    sendEmail: `This action will open email client${actionConfig?.email ? ` to ${actionConfig.email}` : ''}.`,
    copyToClipboard: 'This action will copy text to clipboard.',
    toggleElement: 'This action will show/hide an element on the page.',
    playAnimation: 'This action will play an animation.',
    showBanner: 'This action will display a banner at the top or bottom of the page.',
    showSubscribePopup: 'This action will show a newsletter subscription popup.',
    showCouponCard: 'This action will display a coupon code card.',
    showQRPopup: 'This action will show a QR code popup.'
  };
  
  return messages[actionType] || 'Preview for this action is not available.';
};

export default {
  executeAction,
  isActionPreviewable,
  getPreviewMessage
};